import {Grid, makeStyles} from '@material-ui/core';
import {DateTimeFormat, getCurrentDate, getISODateString, safeRenderDatePickerValue} from 'Quoting/utils/dateUtils';
import {validateNumberOfLives} from 'Quoting/utils/validationHelpers';
import {DateTime} from 'luxon';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {compose} from 'redux';
import {ReducerState} from 'rootReducer';
import {useQuoteField} from '../../hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from '../../shared/GridRow';
import {QuoteFormElement} from '../../shared/QuoteFormElement';
import {FormWrapper} from '../../shared/QuoteFormWrapper';
import {getPolicySummaryPropertyPaths} from './PolicySummaryPropertiesPath';

const usePolicySummaryTabStyles = makeStyles(theme => ({
  paymentSummaryForm: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start',
    },
  },

  paymentSummaryFormGrid: {
    padding: '0 2rem',
  },

  gridRow: {
    minHeight: 'auto',
    [theme.breakpoints.up('sm')]: {
      minHeight: '5.5rem',
    },
  },
}));

export const PolicySummaryTab = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);

  const getDateTimeValue: (value: string) => DateTime | null = compose(safeRenderDatePickerValue, getQuoteFieldValue);

  const policySummaryPropertyPaths = getPolicySummaryPropertyPaths(selectedProduct?.code);

  const UW_OFFER_SEARCH_KEY = `${selectedProduct?.code}PolicyDetail.${
    selectedProduct?.code === 'HI' ? 'brokerUWOffer' : 'quoteUWOffer'
  }`;

  const numberOfLivesValidationResult = validateNumberOfLives(
    getQuoteFieldValue<number>(policySummaryPropertyPaths.numberOfEligibleLives)
  );

  const getDate = (date: string) => getDateTimeValue(date)?.toFormat(DateTimeFormat.DATE);

  const getDateError = (originalDate: string, rateRequestDate: string) =>
    new Date(getDate(rateRequestDate)) < new Date(getDate(originalDate) ?? new Date(getCurrentDate()))
      ? `Rate Request Date >= ${!getDate(originalDate) ? 'Today' : 'Original Rate Request Date'}`
      : '';

  const classes = usePolicySummaryTabStyles();
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'policy-summary-row-1'},
      items: [
        {
          key: 'quote-creation-date',
          element: (
            <QuoteFormElement
              elementType="DatePicker"
              id={policySummaryPropertyPaths.policyCreateDate}
              label="Quote Creation Date"
              value={getDateTimeValue(policySummaryPropertyPaths.policyCreateDate)}
              onChange={date =>
                updateQuoteFieldValue(policySummaryPropertyPaths.policyCreateDate, getISODateString(date))
              }
              disabled
              required
            />
          ),
        },
        {
          key: 'policy-effective-date',
          element: (
            <QuoteFormElement
              elementType="DatePicker"
              placeholder="MM/DD/YYYY"
              id={policySummaryPropertyPaths.policyEffectiveDate}
              label="Policy Effective Date"
              value={getDateTimeValue(policySummaryPropertyPaths.policyEffectiveDate)}
              onChange={date =>
                updateQuoteFieldValue(policySummaryPropertyPaths.policyEffectiveDate, getISODateString(date))
              }
              disabled
              required
            />
          ),
        },
        {
          key: 'isSelfBill',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              id={policySummaryPropertyPaths.isSelfBill}
              label="Self Admin"
              checked={getQuoteFieldValue<boolean>(policySummaryPropertyPaths.isSelfBill)}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.isSelfBill, e.target.checked)}
              disabled
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'policy-summary-row-2'},
      items: [
        {
          key: 'original-rate-request-date',
          element: (
            <QuoteFormElement
              elementType="DatePicker"
              id={policySummaryPropertyPaths.originalRateRequestDate}
              label="Original Rate Request Date"
              value={getDateTimeValue(policySummaryPropertyPaths.originalRateRequestDate)}
              onChange={date =>
                updateQuoteFieldValue(policySummaryPropertyPaths.originalRateRequestDate, getISODateString(date))
              }
              disabled
            />
          ),
        },
        {
          key: 'rate-request-date',
          element: (
            <QuoteFormElement
              elementType="DatePicker"
              id={policySummaryPropertyPaths.rateRequestDate}
              label="Rate Request Date"
              value={getDateTimeValue(policySummaryPropertyPaths.rateRequestDate)}
              onChange={date =>
                updateQuoteFieldValue(policySummaryPropertyPaths.rateRequestDate, getISODateString(date))
              }
              helperText={getDateError(
                policySummaryPropertyPaths.originalRateRequestDate,
                policySummaryPropertyPaths.rateRequestDate
              )}
              error={
                !!getDateError(
                  policySummaryPropertyPaths.originalRateRequestDate,
                  policySummaryPropertyPaths.rateRequestDate
                )
              }
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'policy-summary-row-3'},
      items: [
        {
          key: 'delivery-model',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="Lookup"
              searchKey={`${selectedProduct?.code}DeliveryModel`}
              label="Delivery Model"
              id={policySummaryPropertyPaths.deliveryModelCd}
              value={getQuoteFieldValue<string>(policySummaryPropertyPaths.deliveryModelCd)}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.deliveryModelCd, e.target.value as string)}
              disabled
              required
            />
          ),
        },
        {
          key: 'situs-state',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="Lookup"
              searchKey={`${selectedProduct?.code}SitusState`}
              label="Situs State"
              id={policySummaryPropertyPaths.riskStateCd}
              value={getQuoteFieldValue<string>(policySummaryPropertyPaths.riskStateCd) ?? ''}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.riskStateCd, e.target.value as string)}
              disabled
              required
            />
          ),
        },
        {
          key: 'num-of-eligible-lives',
          element: (
            <QuoteFormElement
              id={policySummaryPropertyPaths.numberOfEligibleLives}
              elementType="Text"
              type="number"
              label="Number of Eligible Lives"
              value={getQuoteFieldValue<number>(policySummaryPropertyPaths.numberOfEligibleLives)}
              onChange={e => {
                const value = e.target.value === '' ? 0 : Number(e.target.value);
                if (!isNaN(value)) {
                  updateQuoteFieldValue(policySummaryPropertyPaths.numberOfEligibleLives, value);
                }
              }}
              error={Boolean(numberOfLivesValidationResult?.error)}
              helperText={numberOfLivesValidationResult?.errorMessage}
              disabled
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'policy-summary-row-4', hidden: true},
      items: [
        {
          key: 'is-prior-coverage',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              id={policySummaryPropertyPaths.isPriorCoverage}
              label="Prior Coverage"
              checked={getQuoteFieldValue<boolean>(policySummaryPropertyPaths.isPriorCoverage)}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.isPriorCoverage, e.target.checked)}
              disabled
              required
            />
          ),
        },
        {
          key: 'prior-carrier-name',
          hidden: true,
          element: (
            <>
              {getQuoteFieldValue<boolean>(policySummaryPropertyPaths.isPriorCoverage) && (
                <QuoteFormElement
                  elementType="Text"
                  label="Prior Carrier Name"
                  id={policySummaryPropertyPaths.priorCarrierName}
                  value={getQuoteFieldValue<string>(policySummaryPropertyPaths.priorCarrierName)}
                  // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.priorCarrierName, e.target.value)}
                  disabled
                  required
                />
              )}
            </>
          ),
        },
        {
          key: 'prior-coverage-number',
          hidden: true,
          element: (
            <>
              {getQuoteFieldValue<boolean>(policySummaryPropertyPaths.isPriorCoverage) && (
                <QuoteFormElement
                  elementType="Text"
                  label="Prior Coverage Number"
                  id={policySummaryPropertyPaths.priorCoverageNo}
                  value={getQuoteFieldValue<string>(policySummaryPropertyPaths.priorCoverageNo)}
                  // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.priorCoverageNo, e.target.value)}
                  disabled
                  required
                />
              )}
            </>
          ),
        },
      ],
    },
    {
      rowProps: {key: 'policy-summary-row-5'},
      items: [
        {
          key: 'quote.sicCode',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="SIC Code"
              id={policySummaryPropertyPaths.sicCode}
              value={getQuoteFieldValue<string>(policySummaryPropertyPaths.sicCode)}
              onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.sicCode, e.target.value)}
              disabled
              required
            />
          ),
        },
        {
          key: 'quote.sicDescription',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="SIC Description"
              id={policySummaryPropertyPaths.sicDescription}
              value={getQuoteFieldValue<string>(policySummaryPropertyPaths.sicDescription)}
              onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.sicDescription, e.target.value)}
              disabled
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'policy-summary-row-6'},
      items: [
        {
          key: 'quote.policyDetail.brokerUWOffer',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Quote UW Offer"
              searchKey={UW_OFFER_SEARCH_KEY}
              id={policySummaryPropertyPaths.uwOffer}
              value={getQuoteFieldValue<string>(policySummaryPropertyPaths.uwOffer)}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.uwOffer, e.target.value as string)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 4},
        },
        {
          key: 'is-power-broker',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              id={policySummaryPropertyPaths.isPowerBroker}
              label="Power Broker"
              checked={getQuoteFieldValue<boolean>(policySummaryPropertyPaths.isPowerBroker)}
              // onChange={e => updateQuoteFieldValue(policySummaryPropertyPaths.isSelfBill, e.target.checked)}
              disabled
              required
            />
          ),
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Policy" collapsible expanded>
      <Grid container alignItems="center" className={classes.paymentSummaryFormGrid}>
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
